import { Component, OnInit } from '@angular/core';
import { SystemResetService, SystemResetSummaryDTO, SystemResetResponseDTO } from '../../../services/system-reset.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-system-reset',
  templateUrl: './system-reset.component.html',
  styleUrls: ['./system-reset.component.scss']
})
export class SystemResetComponent implements OnInit {

  resetSummary: SystemResetSummaryDTO | null = null;
  resetResponse: SystemResetResponseDTO | null = null;
  showSimulation = false;
  showResetForm = false;
  showResults = false;
  
  resetForm: FormGroup;
  isResetting = false;

  constructor(
    private systemResetService: SystemResetService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private fb: FormBuilder
  ) {
    this.resetForm = this.fb.group({
      confirmationKey: ['', [Validators.required]],
      dryRun: [false]
    });
  }

  ngOnInit(): void {
    // Component initialization
  }

  /**
   * Simula el reseteo mostrando qué datos serán eliminados
   */
  simulateReset(): void {
    this.loadingService.show();
    this.systemResetService.getResetSummary().subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.resetSummary = response.data;
          this.showSimulation = true;
          this.showResetForm = false;
          this.showResults = false;
          this.resetResponse = null;
          this.loadingService.hide();
        } else {
          this.toastService.showToast('error', 'Error al obtener el resumen del sistema');
          this.loadingService.hide();
        }
      },
      error: (error) => {
        console.error('Error simulating reset:', error);
        this.toastService.showToast('error', 'Error al simular el reseteo del sistema');
        this.loadingService.hide();
      }
    });
  }

  /**
   * Muestra el formulario para ejecutar el reset real
   */
  showResetFormMethod(): void {
    if (!this.resetSummary) {
      this.simulateReset();
    }
    this.showResetForm = true;
  }

  /**
   * Ejecuta el reset real del sistema
   */
  executeReset(): void {
    if (this.resetForm.invalid || this.isResetting) {
      this.resetForm.markAllAsTouched();
      return;
    }

    const confirmationKey = this.resetForm.get('confirmationKey')?.value;
    const dryRun = this.resetForm.get('dryRun')?.value || false;
    
    // Confirmación adicional del usuario solo si no es dry run
    if (!dryRun) {
      const userConfirmation = confirm(
        '⚠️ ADVERTENCIA: Esta acción eliminará TODOS los datos del sistema de manera permanente.\n\n' +
        'Esta operación NO SE PUEDE DESHACER.\n\n' +
        '¿Está absolutamente seguro de que desea continuar?'
      );

      if (!userConfirmation) {
        return;
      }
    }

    this.isResetting = true;
    this.loadingService.show();

    this.systemResetService.resetSystem(confirmationKey, dryRun).subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.resetResponse = response.data;
          this.showResults = true;
          this.showResetForm = false;
          this.resetForm.reset();
          
          const successMessage = dryRun 
            ? 'Simulación completada exitosamente' 
            : 'Sistema reseteado exitosamente';
          this.toastService.showToast('success', successMessage);
        } else {
          this.toastService.showToast('error', response.message || 'Error al resetear el sistema');
        }
        this.isResetting = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error executing reset:', error);
        this.toastService.showToast('error', 'Error al ejecutar el reseteo del sistema');
        this.isResetting = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Reinicia la vista a su estado inicial
   */
  resetView(): void {
    this.resetSummary = null;
    this.resetResponse = null;
    this.showSimulation = false;
    this.showResetForm = false;
    this.showResults = false;
    this.resetForm.reset({
      confirmationKey: '',
      dryRun: false
    });
  }

  /**
   * Verifica si un campo del formulario tiene un error específico
   */
  hasError(controlName: string, errorType: string): boolean {
    const control = this.resetForm.get(controlName);
    return control?.touched && control?.hasError(errorType) || false;
  }

  /**
   * Formatea números grandes con separadores de miles
   */
  formatNumber(num: number): string {
    return num.toLocaleString('es-ES');
  }
}
